import { atom } from "jotai";
import { atomEffect } from "jotai-effect";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { $isAuthenticated } from "../authenication";
import { $collabRegistryConnection } from "../reboot/collab-registry-connection.atom";
import { $internet } from "../utils";
import { $workspaces } from "../workspaces";

const logger = new Logger("document-collab-registry");

// Types, mostly matching those in registry.proto.
type CollabSessionInfo = { branch: string; sha: string; status: string };
type DocumentCollabInfo = { sessions: Record<string, CollabSessionInfo> };
type WorkspaceCollabSessions = Record<string, DocumentCollabInfo>;

// Collab sessions stored per workspace. Exported for optimistic updates when sharing.
export const $collabSessionsStore = atomFamily((workspaceSlug: string) => {
	const storeAtom = atom({} as WorkspaceCollabSessions);
	storeAtom.debugLabel = `collabSessionsStore/${workspaceSlug}`;
	return storeAtom;
});

// Track active subscriptions.
const activeSubscriptions = new Set<string>();

/**
 * Subscribe to updates for collab sessions for every workspace we know about.
 */
export const $collabRegistryEffect = atomEffect((get, set) => {
	// Don't start subscription if we are either not logged in or not connected to the internet.
	// NOTE: If we go offline or become unauthenticated, the subscriptions will NOT be stopped, yet.
	const internet = get($internet);
	const authenticated = get($isAuthenticated);
	if (!internet || !authenticated) {
		return;
	}

	const workspacesState = get($workspaces);
	if (!("workspaces" in workspacesState)) {
		return;
	}

	for (const workspace of workspacesState.workspaces) {
		if (activeSubscriptions.has(workspace.slug)) {
			continue;
		}

		const connection = get($collabRegistryConnection(workspace.slug));
		if (!connection) {
			continue;
		}

		activeSubscriptions.add(workspace.slug);

		// Start subscription.
		void (async () => {
			try {
				logger.info("Starting collab sessions subscription", {
					workspaceSlug: workspace.slug,
				});

				const [generator] = await connection.registry
					.reactively()
					.collabSessions(connection.context, {});

				for await (const response of generator) {
					set($collabSessionsStore(workspace.slug), response.documents);
					logger.debug("Updated collab sessions", {
						workspaceSlug: workspace.slug,
						documentCount: Object.keys(response.documents).length,
					});
				}
			} catch (error) {
				logger.error("Collab sessions subscription error", {
					workspaceSlug: workspace.slug,
					error,
				});
				activeSubscriptions.delete(workspace.slug);
			}
		})();
	}
});
$collabRegistryEffect.debugLabel = "collabRegistryEffect";

// Public accessor.
export const $workspaceCollabSessions = atom((get) => {
	const workspacesState = get($workspaces);
	if (!("workspaces" in workspacesState)) {
		return {};
	}

	return Object.fromEntries(
		workspacesState.workspaces.map((workspace) => [
			workspace.slug,
			get($collabSessionsStore(workspace.slug)),
		])
	);
});
$workspaceCollabSessions.debugLabel = `workspaceCollabSessions`;
